<template>
  <div class="mood-wall">
    <!-- 主画布 -->
    <canvas ref="mainCanvas" class="main-canvas"></canvas>
    
    <!-- 爆炸计数 -->
    <div class="pop-counter" v-if="popCount > 0">
      <span class="pop-emoji">💥</span>
      <span class="pop-num">{{ popCount }}</span>
    </div>

    <!-- 控制按钮 -->
    <div class="controls">
      <button class="ctrl-btn sound-btn" @click="toggleSound">
        {{ soundEnabled ? '🔊' : '🔇' }}
      </button>
      <button class="ctrl-btn refresh-btn" @click="refreshBubbles">🔄</button>
      <button class="ctrl-btn publish-btn" @click="showModal = true">
        <span>✨ 释放心情</span>
      </button>
    </div>

    <!-- 发布弹窗 -->
    <Teleport to="body">
      <div v-if="showModal" class="modal-mask" @click.self="showModal = false">
        <div class="modal-box">
          <div class="modal-top">
            <span class="modal-emoji">💭</span>
            <span class="modal-title">说点什么</span>
            <button class="modal-close" @click="showModal = false">✕</button>
          </div>
          <div class="modal-content">
            <textarea 
              v-model="inputText" 
              placeholder="匿名发布，释放情绪..." 
              maxlength="100"
            ></textarea>
            <div class="word-count">{{ inputText.length }}/100</div>
          </div>
          <div class="modal-bottom">
            <button class="modal-btn cancel" @click="showModal = false">取消</button>
            <button class="modal-btn submit" @click="submitMsg" :disabled="!inputText.trim()">发送</button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted } from 'vue'
import { ElMessage } from 'element-plus'
import * as moodApi from '../api/mood'

export default {
  name: 'MoodWall',
  setup() {
    const mainCanvas = ref(null)
    const showModal = ref(false)
    const inputText = ref('')
    const popCount = ref(0)
    const soundEnabled = ref(true)

    let ctx, offscreen, offCtx
    let width, height, dpr
    let bubbles = []
    let particles = []
    let stars = []
    let animationId = null
    let audioCtx = null
    let messages = []

    // 颜色方案
    const colors = [
      { bg: 'rgba(102, 126, 234, 0.85)', glow: '#667eea' },
      { bg: 'rgba(118, 75, 162, 0.85)', glow: '#764ba2' },
      { bg: 'rgba(240, 98, 146, 0.85)', glow: '#f06292' },
      { bg: 'rgba(77, 208, 225, 0.85)', glow: '#4dd0e1' },
      { bg: 'rgba(129, 199, 132, 0.85)', glow: '#81c784' },
      { bg: 'rgba(255, 183, 77, 0.85)', glow: '#ffb74d' },
      { bg: 'rgba(186, 104, 200, 0.85)', glow: '#ba68c8' },
      { bg: 'rgba(79, 195, 247, 0.85)', glow: '#4fc3f7' },
    ]

    // 初始化画布
    const initCanvas = () => {
      const canvas = mainCanvas.value
      const rect = canvas.parentElement.getBoundingClientRect()
      dpr = window.devicePixelRatio || 1
      width = rect.width
      height = rect.height

      canvas.width = width * dpr
      canvas.height = height * dpr
      canvas.style.width = width + 'px'
      canvas.style.height = height + 'px'

      ctx = canvas.getContext('2d')
      ctx.scale(dpr, dpr)

      // 离屏画布（双缓冲）
      offscreen = document.createElement('canvas')
      offscreen.width = width * dpr
      offscreen.height = height * dpr
      offCtx = offscreen.getContext('2d')
      offCtx.scale(dpr, dpr)

      initStars()
    }

    // 初始化星星
    const initStars = () => {
      stars = []
      for (let i = 0; i < 100; i++) {
        stars.push({
          x: Math.random() * width,
          y: Math.random() * height,
          r: Math.random() * 1.5 + 0.5,
          alpha: Math.random(),
          speed: Math.random() * 0.02 + 0.01
        })
      }
    }

    // 创建气泡
    const createBubble = (msg, index) => {
      const colorScheme = colors[index % colors.length]
      const r = 45 + Math.random() * 25
      return {
        id: msg.id,
        text: msg.content,
        x: r + Math.random() * (width - r * 2),
        y: r + Math.random() * (height - r * 2 - 100),
        r,
        vx: (Math.random() - 0.5) * 0.5,
        vy: (Math.random() - 0.5) * 0.5,
        color: colorScheme.bg,
        glow: colorScheme.glow,
        scale: 1,
        alpha: 1,
        popping: false
      }
    }

    // 加载消息
    const loadMessages = async () => {
      try {
        const res = await moodApi.getMessages(20)
        if (res.data.code === 200) {
          messages = res.data.data || []
          bubbles = messages.map((msg, i) => createBubble(msg, i))
        }
      } catch (e) {
        console.error(e)
      }
    }

    // 绘制星空背景
    const drawBackground = () => {
      // 渐变背景
      const gradient = offCtx.createLinearGradient(0, 0, width, height)
      gradient.addColorStop(0, '#0f0c29')
      gradient.addColorStop(0.5, '#302b63')
      gradient.addColorStop(1, '#24243e')
      offCtx.fillStyle = gradient
      offCtx.fillRect(0, 0, width, height)

      // 星星
      stars.forEach(star => {
        star.alpha += star.speed
        const a = Math.abs(Math.sin(star.alpha))
        offCtx.beginPath()
        offCtx.arc(star.x, star.y, star.r, 0, Math.PI * 2)
        offCtx.fillStyle = `rgba(255, 255, 255, ${a * 0.8})`
        offCtx.fill()
      })
    }

    // 绘制气泡
    const drawBubble = (b) => {
      if (b.alpha <= 0) return

      offCtx.save()
      offCtx.globalAlpha = b.alpha
      offCtx.translate(b.x, b.y)
      offCtx.scale(b.scale, b.scale)

      // 发光效果
      offCtx.shadowColor = b.glow
      offCtx.shadowBlur = 20

      // 气泡主体
      offCtx.beginPath()
      offCtx.arc(0, 0, b.r, 0, Math.PI * 2)
      offCtx.fillStyle = b.color
      offCtx.fill()

      // 高光
      offCtx.shadowBlur = 0
      const hlGradient = offCtx.createRadialGradient(-b.r * 0.3, -b.r * 0.3, 0, -b.r * 0.3, -b.r * 0.3, b.r * 0.5)
      hlGradient.addColorStop(0, 'rgba(255,255,255,0.6)')
      hlGradient.addColorStop(1, 'rgba(255,255,255,0)')
      offCtx.beginPath()
      offCtx.arc(-b.r * 0.3, -b.r * 0.3, b.r * 0.4, 0, Math.PI * 2)
      offCtx.fillStyle = hlGradient
      offCtx.fill()

      // 文字
      offCtx.fillStyle = 'white'
      offCtx.font = `bold ${Math.max(10, b.r / 4)}px -apple-system, sans-serif`
      offCtx.textAlign = 'center'
      offCtx.textBaseline = 'middle'
      
      // 文字换行
      const maxWidth = b.r * 1.4
      const words = b.text
      const lines = []
      let line = ''
      for (let char of words) {
        const testLine = line + char
        if (offCtx.measureText(testLine).width > maxWidth && line) {
          lines.push(line)
          line = char
        } else {
          line = testLine
        }
      }
      lines.push(line)
      
      const lineHeight = Math.max(12, b.r / 3.5)
      const startY = -(lines.length - 1) * lineHeight / 2
      lines.slice(0, 3).forEach((l, i) => {
        offCtx.fillText(l, 0, startY + i * lineHeight)
      })

      offCtx.restore()
    }

    // 绘制粒子
    const drawParticles = () => {
      particles = particles.filter(p => p.life > 0)
      particles.forEach(p => {
        p.x += p.vx
        p.y += p.vy
        p.vy += 0.1 // 重力
        p.life -= 2
        p.r *= 0.96

        offCtx.beginPath()
        offCtx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
        offCtx.fillStyle = `rgba(${p.rgb}, ${p.life / 100})`
        offCtx.fill()
      })
    }

    // 更新气泡位置
    const updateBubbles = () => {
      bubbles.forEach(b => {
        if (b.popping) {
          b.scale *= 0.9
          b.alpha -= 0.08
          return
        }

        b.x += b.vx
        b.y += b.vy

        // 边界反弹
        if (b.x - b.r < 0 || b.x + b.r > width) b.vx *= -1
        if (b.y - b.r < 0 || b.y + b.r > height - 80) b.vy *= -1

        b.x = Math.max(b.r, Math.min(width - b.r, b.x))
        b.y = Math.max(b.r, Math.min(height - 80 - b.r, b.y))
      })

      // 移除已消失的气泡并重新生成
      bubbles = bubbles.filter(b => {
        if (b.alpha <= 0) {
          const idx = messages.findIndex(m => m.id === b.id)
          if (idx !== -1) {
            const newBubble = createBubble(messages[idx], idx)
            setTimeout(() => bubbles.push(newBubble), 500)
          }
          return false
        }
        return true
      })
    }

    // 主渲染循环
    const render = () => {
      offCtx.clearRect(0, 0, width, height)
      
      drawBackground()
      bubbles.forEach(drawBubble)
      drawParticles()

      // 双缓冲：将离屏画布绘制到主画布
      ctx.clearRect(0, 0, width, height)
      ctx.drawImage(offscreen, 0, 0, width * dpr, height * dpr, 0, 0, width, height)

      updateBubbles()
      animationId = requestAnimationFrame(render)
    }

    // 点击检测
    const handleClick = (e) => {
      const rect = mainCanvas.value.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top

      for (let i = bubbles.length - 1; i >= 0; i--) {
        const b = bubbles[i]
        if (b.popping) continue
        const dist = Math.sqrt((x - b.x) ** 2 + (y - b.y) ** 2)
        if (dist < b.r) {
          popBubble(b)
          break
        }
      }
    }

    // 戳破气泡
    const popBubble = (b) => {
      b.popping = true
      popCount.value++
      playSound()
      createExplosion(b)
    }

    // 创建爆炸粒子
    const createExplosion = (b) => {
      const rgb = hexToRgb(b.glow)
      for (let i = 0; i < 15; i++) {
        const angle = (Math.PI * 2 / 15) * i
        const speed = 3 + Math.random() * 4
        particles.push({
          x: b.x,
          y: b.y,
          vx: Math.cos(angle) * speed,
          vy: Math.sin(angle) * speed,
          r: 4 + Math.random() * 4,
          rgb,
          life: 100
        })
      }
    }

    const hexToRgb = (hex) => {
      const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
      return result ? `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(result[3], 16)}` : '255,255,255'
    }

    // 音效 - 移动端必须在用户交互时初始化
    let audioInitialized = false
    
    const initAudio = () => {
      if (audioInitialized) return
      try {
        audioCtx = new (window.AudioContext || window.webkitAudioContext)()
        // 立即尝试恢复（移动端需要）
        if (audioCtx.state === 'suspended') {
          audioCtx.resume()
        }
        audioInitialized = true
      } catch (e) {
        console.log('Audio init failed:', e)
      }
    }

    const playSound = () => {
      if (!soundEnabled.value) return
      
      // 延迟初始化：在用户交互时创建 AudioContext
      if (!audioCtx) {
        initAudio()
      }
      
      if (!audioCtx) return
      
      try {
        // 每次播放前确保恢复
        if (audioCtx.state === 'suspended') {
          audioCtx.resume()
        }
        
        const osc = audioCtx.createOscillator()
        const gain = audioCtx.createGain()
        osc.connect(gain)
        gain.connect(audioCtx.destination)
        osc.frequency.setValueAtTime(600, audioCtx.currentTime)
        osc.frequency.exponentialRampToValueAtTime(150, audioCtx.currentTime + 0.15)
        gain.gain.setValueAtTime(0.3, audioCtx.currentTime)
        gain.gain.exponentialRampToValueAtTime(0.01, audioCtx.currentTime + 0.15)
        osc.start(audioCtx.currentTime)
        osc.stop(audioCtx.currentTime + 0.15)
      } catch (e) {
        console.log('Play sound failed:', e)
      }
    }

    const toggleSound = () => {
      // 点击音效按钮时初始化音频（用户交互触发）
      if (!audioCtx) {
        initAudio()
      }
      soundEnabled.value = !soundEnabled.value
    }

    const refreshBubbles = () => {
      loadMessages()
      popCount.value = 0
    }

    const submitMsg = async () => {
      if (!inputText.value.trim()) return
      try {
        const res = await moodApi.addMessage(inputText.value.trim())
        if (res.data.code === 200) {
          ElMessage.success('已飘向词海~')
          showModal.value = false
          inputText.value = ''
          loadMessages()
        } else {
          ElMessage.error(res.data.message || '发布失败')
        }
      } catch (e) {
        ElMessage.error('发布失败')
      }
    }

    const handleResize = () => {
      initCanvas()
      bubbles.forEach(b => {
        b.x = Math.min(b.x, width - b.r)
        b.y = Math.min(b.y, height - 80 - b.r)
      })
    }

    onMounted(() => {
      initCanvas()
      // 不在这里初始化音频，等用户第一次交互时再初始化
      loadMessages()
      render()
      mainCanvas.value.addEventListener('click', handleClick)
      mainCanvas.value.addEventListener('touchstart', (e) => {
        e.preventDefault()
        const touch = e.touches[0]
        handleClick({ clientX: touch.clientX, clientY: touch.clientY })
      }, { passive: false })
      window.addEventListener('resize', handleResize)
    })

    onUnmounted(() => {
      cancelAnimationFrame(animationId)
      window.removeEventListener('resize', handleResize)
    })

    return {
      mainCanvas, showModal, inputText, popCount, soundEnabled,
      toggleSound, refreshBubbles, submitMsg
    }
  }
}
</script>

<style scoped>
.mood-wall {
  position: relative;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 120px);
  overflow: hidden;
  background: #0f0c29;
}

.main-canvas {
  display: block;
  width: 100%;
  height: 100%;
  min-height: calc(100vh - 120px);
  touch-action: none;
}

/* 爆炸计数 */
.pop-counter {
  position: fixed;
  top: 76px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 18px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  z-index: 10;
}

.pop-emoji { font-size: 18px; }
.pop-num {
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
}

/* 控制按钮 */
.controls {
  position: fixed;
  bottom: 90px;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 12px;
  padding: 0 20px;
  z-index: 10;
}

.ctrl-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  transition: transform 0.15s ease, opacity 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.ctrl-btn:active {
  transform: scale(0.92);
  opacity: 0.85;
}

.sound-btn, .refresh-btn {
  width: 46px;
  height: 46px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(12px);
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.15);
  font-size: 20px;
}

.publish-btn {
  padding: 14px 28px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 28px;
  color: #fff;
  font-size: 15px;
  font-weight: 600;
  box-shadow: 0 6px 24px rgba(102, 126, 234, 0.35);
}

/* 弹窗 */
.modal-mask {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.65);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
}

.modal-box {
  width: 100%;
  max-width: 380px;
  background: linear-gradient(160deg, #1f1f2e 0%, #2a2a40 100%);
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  animation: modalPop 0.25s ease;
}

@keyframes modalPop {
  from { opacity: 0; transform: scale(0.92) translateY(10px); }
  to { opacity: 1; transform: scale(1) translateY(0); }
}

.modal-top {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 18px 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}

.modal-emoji { font-size: 24px; }
.modal-title {
  flex: 1;
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}

.modal-close {
  width: 30px;
  height: 30px;
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 50%;
  color: rgba(255, 255, 255, 0.6);
  font-size: 16px;
  cursor: pointer;
}

.modal-content {
  padding: 20px;
}

.modal-content textarea {
  width: 100%;
  height: 100px;
  padding: 14px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  color: #fff;
  font-size: 15px;
  resize: none;
  outline: none;
  transition: border-color 0.2s;
}

.modal-content textarea::placeholder {
  color: rgba(255, 255, 255, 0.35);
}

.modal-content textarea:focus {
  border-color: #667eea;
}

.word-count {
  text-align: right;
  margin-top: 8px;
  color: rgba(255, 255, 255, 0.4);
  font-size: 12px;
}

.modal-bottom {
  display: flex;
  gap: 12px;
  padding: 16px 20px 20px;
}

.modal-btn {
  flex: 1;
  padding: 13px;
  border: none;
  border-radius: 12px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.15s;
}

.modal-btn.cancel {
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
}

.modal-btn.submit {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
}

.modal-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

/* 移动端 */
@media (max-width: 768px) {
  .mood-wall {
    min-height: calc(100vh - 116px);
  }
  
  .main-canvas {
    min-height: calc(100vh - 116px);
  }
  
  .pop-counter {
    top: 66px;
    padding: 6px 14px;
  }
  
  .controls {
    bottom: 76px;
    gap: 10px;
  }
  
  .sound-btn, .refresh-btn {
    width: 42px;
    height: 42px;
    font-size: 18px;
  }
  
  .publish-btn {
    padding: 12px 22px;
    font-size: 14px;
  }
}
</style>
